import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  PlatformCompaniesListParams,
  PlatformCompaniesListResult,
  PlatformCompanyResponse,
  CreatePlatformCompanyData,
  CreatePlatformCompanyResult,
  UpdatePlatformCompanyData,
} from '../types/platformAdmin';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('platform_token')}`,
  'Content-Type': 'application/json',
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getPlatformCompanies = async (
  params?: PlatformCompaniesListParams
): Promise<PlatformCompaniesListResult> => {
  const queryParams = new URLSearchParams();

  if (params?.search) {
    queryParams.set('search', params.search);
  }
  if (params?.active != null) {
    queryParams.set('active', params.active ? '1' : '0');
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.sort) {
    queryParams.set('sort', params.sort);
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PLATFORM.COMPANIES}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch companies');
  }

  return response.json();
};

export const getPlatformCompany = async (id: number): Promise<PlatformCompanyResponse> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PLATFORM.COMPANIES}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch company');
  }

  return response.json();
};

export const createPlatformCompany = async (
  data: CreatePlatformCompanyData
): Promise<CreatePlatformCompanyResult> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PLATFORM.COMPANIES}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create company');
  }

  return response.json();
};

export const updatePlatformCompany = async (
  id: number,
  data: UpdatePlatformCompanyData
): Promise<PlatformCompanyResponse> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PLATFORM.COMPANIES}/${id}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update company');
  }

  return response.json();
};
